/**
 * The automaton: (state, route, fix) → state. No timers, no store, no DOM.
 *
 * Holding a condition for ten seconds is a COMPARISON of `fix.at` against a
 * mark in the state, never a `setTimeout` — which is what lets a test push
 * forty fixes through it in a millisecond and assert the exact number of
 * arrivals, and what keeps a backgrounded tab honest: iOS throttles
 * `watchPosition` and then delivers a burst of catch-up fixes, and a machine
 * clocked on `Date.now()` would read that burst as ten seconds of standing
 * still at every stop it passed.
 *
 * Three properties this file exists to hold, all of them measured against a
 * visitor rather than against a spec:
 *
 *  * **A fix with `accuracyM` over the ceiling is dropped WHOLE.** Urban GPS in
 *    a park routinely reports 100–300 m, and such a fix does not merely add
 *    noise — it teleports the visitor off the path and fires an off-route
 *    event while they are standing on it.
 *  * **A stop is arrived at ONCE.** Somebody who walks back to Самсон must not
 *    be told they have arrived a second time, so `arrived` is a set and it is
 *    never cleared on the way back.
 *  * **Leaving the route is reported once, with hysteresis.** The flag comes
 *    down only inside `backOnRouteM`, well inside `offRouteM`, or a visitor
 *    walking along the 40 m boundary would collect an event per fix.
 */

import type { MapCardProps } from '../catalog/generated/schemas';
import { pointsToLngLat, toLngLat, type LngLat } from '../map/objects';
import { anchorStops, cumulative, distanceAlongTo, distanceM, snapToPolyline, type Snap } from './geo';

export type Fix = {
  /** `position.timestamp`. The automaton's ONLY clock. */
  at: number;
  lng: number;
  lat: number;
  accuracyM: number;
  headingDeg: number | null;
  speedMs: number | null;
};

export type StopModel = {
  id: string;
  label: string;
  at: LngLat;
  /**
   * The stop's `n`, which is its identity ON THIS WIRE.
   *
   * `current` is an `n` and `legs[].n_to` is an `n`, so a model keyed on the
   * pin's `id` would need a lookup at every one of those joins. The id stays
   * the local key (it is what `arrived` holds) and `n` is what travels.
   */
  n: number;
  /**
   * The board element this pin STANDS FOR («obj:40»), when there is one.
   *
   * It is what lets a tap open the card the guide has already put on the board
   * instead of asking the model about an object it has just described — a
   * SCROLL, locally, with no run behind it.
   */
  ref: string | null;
};

/** 2GIS's own word for how a leg's seconds were arrived at. */
export type LegSource = 'dgis' | 'dgis_split' | 'cache' | 'straight';

export type LegModel = {
  /** `n` of the pin this leg starts at. 0 is the visitor's own position. */
  nFrom: number;
  nTo: number;
  /** Vertices of `route.points`, when the backend said so rather than us guessing. */
  first: number | null;
  last: number | null;
  distanceM: number;
  durationS: number | null;
  source: LegSource;
  /** «park_path», «stairway», «pedestrian_bridge» — 2GIS's word, not ours. */
  style: string | null;
};

export type RouteModel = {
  line: LngLat[];
  cum: number[];
  stops: StopModel[];
  /** Vertex of `line` each stop stands at, same order as `stops`. */
  anchors: number[];
  /** The leg ARRIVING at `stops[i]`, when there is one. Same order as `stops`. */
  legs: (LegModel | null)[];
};

/**
 * The minutes for a leg, or `null` when there are none to print.
 *
 * **Only `source: "dgis"` may be printed as an exact figure. Everything else
 * wears a «~».** That is the owner's rule and it is one line wider than the
 * backend's own distinction, which separates `cache` (2GIS's number, read back
 * from a cache) and `dgis_split` (a share of a longer route) and `straight` (a
 * line on the map and a walking pace, with no router in it at all). The blanket
 * rule is the safe direction of that disagreement: it can only ever mark a real
 * measurement as approximate, never the reverse, and the reverse is the one
 * that costs somebody a museum they arrive at closed.
 *
 * The seconds themselves are always the BACKEND's. The frontend has never
 * computed a walking time and must not start: the repository's oldest promise
 * about time is that the only minutes a plan prints are the router's.
 */
export function minutesLabel(leg: LegModel | null | undefined): string | null {
  if (!leg || leg.durationS === null) return null;
  const minutes = Math.max(1, Math.round(leg.durationS / 60));
  return leg.source === 'dgis' ? `${minutes} мин` : `~${minutes} мин`;
}

/**
 * The walk as a whole: «2,1 км · ~36 мин».
 *
 * `total_duration_s` is described on the wire as 2GIS's own aggregate and
 * «exact when it is here» — but a route can carry a `straight` leg and still
 * report a total, so the total is called exact only when EVERY leg was measured
 * by the router. Anything else is «~». One rule, asked of the whole route
 * rather than of the field's name.
 */
export function totalsLabel(props: {
  total_distance_m?: number | undefined;
  total_duration_s?: number | undefined;
  legs?: readonly { source: string }[] | undefined;
}): string | null {
  const parts: string[] = [];
  if (typeof props.total_distance_m === 'number') {
    parts.push(
      props.total_distance_m >= 1000
        ? `${(props.total_distance_m / 1000).toFixed(1)} км`
        : `${props.total_distance_m} м`,
    );
  }
  if (typeof props.total_duration_s === 'number') {
    const minutes = Math.max(1, Math.round(props.total_duration_s / 60));
    const measured = (props.legs ?? []).length > 0 && (props.legs ?? []).every((leg) => leg.source === 'dgis');
    parts.push(measured ? `${minutes} мин` : `~${minutes} мин`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** A leg the visitor climbs rather than walks. */
export const STAIRWAY = 'stairway';

export type DashedSegment = { key: string; points: LngLat[] };

/**
 * The stretches to draw dashed — today, the stairways.
 *
 * Pedestrian Routing at 2GIS returns only `begin`/`end` manoeuvres, so there is
 * no turn-by-turn to give and none is invented. `style` is the whole of what
 * this wire can say about the WAY, and a flight of steps is the one thing a
 * visitor with a pushchair has to know before they are standing at the bottom
 * of it.
 *
 * A leg with no `first`/`last` is skipped rather than guessed at: the dash is a
 * claim about a specific stretch of path, and the projection that would place
 * it is exactly the guess `legs` exists to remove.
 */
export function stairwaySegments(route: RouteModel): DashedSegment[] {
  const out: DashedSegment[] = [];
  for (const leg of route.legs) {
    if (!leg || leg.style !== STAIRWAY) continue;
    if (leg.first === null || leg.last === null) continue;
    const from = Math.max(0, Math.min(leg.first, route.line.length - 1));
    const to = Math.max(0, Math.min(leg.last, route.line.length - 1));
    const points = route.line.slice(Math.min(from, to), Math.max(from, to) + 1);
    if (points.length < 2) continue;
    out.push({ key: `stairway:${leg.nFrom}-${leg.nTo}`, points });
  }
  return out;
}

export type TrackConfig = {
  arriveRadiusM: number;
  arriveHoldMs: number;
  offRouteM: number;
  offRouteHoldMs: number;
  /** Hysteresis: the flag comes down well inside the threshold that raised it. */
  backOnRouteM: number;
  /** Worse than this and the fix is not evidence of anything. */
  maxAccuracyM: number;
};

export const DEFAULT_TRACK_CONFIG: TrackConfig = {
  arriveRadiusM: 25,
  arriveHoldMs: 10_000,
  offRouteM: 40,
  offRouteHoldMs: 30_000,
  backOnRouteM: 25,
  maxAccuracyM: 50,
};

export type NavSignal =
  | { kind: 'arrived'; stopId: string; index: number; distanceM: number }
  | { kind: 'off_route'; distanceM: number; nearestStopId: string | null }
  /** Local only — the guide is not told that somebody came back. */
  | { kind: 'back_on_route' };

export type TrackState = {
  snap: Snap | null;
  /** Index into `stops` of the leg being walked. */
  currentLeg: number;
  /** Index into `stops`, or `null` when the walk is done. */
  nextStop: number | null;
  /** Metres to `nextStop` — see `distanceToStop`. */
  toNextM: number | null;
  /**
   * Is `toNextM` a distance along the DRAWN path, or partly a straight line?
   *
   * A visitor standing off the route walks TO it before they walk along it,
   * and nobody routed that first stretch. Printing such a number plainly is
   * the same claim as printing an unrouted walking time.
   */
  toNextExact: boolean;
  /** Stop ids reached, in order. Never emptied. */
  arrived: string[];
  offRoute: boolean;
  /** What the caller should tell the guide, if anything. */
  emit: NavSignal | null;
  /** Bookkeeping for the holds — the marks the clock is compared against. */
  nearSince: { stopId: string; since: number } | null;
  offSince: number | null;
};

export function initialTrackState(): TrackState {
  return {
    snap: null,
    currentLeg: 0,
    nextStop: null,
    toNextM: null,
    toNextExact: false,
    arrived: [],
    offRoute: false,
    emit: null,
    nearSince: null,
    offSince: null,
  };
}

/**
 * Builds the route the automaton walks from the card the board is showing.
 *
 * The stops are the pins numbered from ONE, in `n` order.
 *
 * **`n: 0` is the visitor, and it is not a stop.** When the guide leads from
 * where somebody is standing, the wire carries their position as a pin — `kind:
 * "me"`, `n: 0` — so that `legs[].n_from: 0` has something to point at and the
 * first stretch can be drawn. Counting it as a stop costs three separate wrongs
 * at once: it appears in the list of points, `current: 1` marks it «пройдено»
 * (everything below `current` has been reached), and the automaton would
 * announce an arrival at the visitor's own feet. Both halves of the test are
 * kept — the KIND and the number — because either alone is an assumption about
 * the other. Unnumbered pins are out for the older reason: an entrance or a
 * break is a real point on the map and not a place the guide says «вы на
 * месте» about (a route starts at the gates, which are not a stop).
 *
 * A leg joins two `n`s, and `n_from: 0` is the visitor's own position, so the
 * leg ARRIVING at each stop is the one whose `n_to` is that stop's number. Its
 * `first`/`last` are the real vertices of the drawn line; where they are absent
 * the vertices are projected instead, and that is a GUESS — a walk through a
 * park passes near itself, and a projection then anchors a later stop to an
 * earlier vertex and runs the leg backwards. Both paths produce the same shape,
 * so nothing downstream knows which one ran.
 */
export function routeModel(props: MapCardProps): RouteModel | null {
  const line = pointsToLngLat(props.route?.points);

  const numbered = props.pins
    .filter((pin): pin is typeof pin & { n: number } =>
      typeof pin.n === 'number' && pin.n >= 1 && pin.kind !== 'me',
    )
    .sort((a, b) => a.n - b.n);
  if (numbered.length === 0) return null;

  const stops: StopModel[] = numbered.map((pin) => ({
    id: pin.id,
    label: pin.label,
    at: toLngLat(pin),
    n: pin.n,
    ref: pin.ref ?? null,
  }));

  const byArrival = new Map<number, LegModel>();
  for (const leg of props.legs ?? []) {
    byArrival.set(leg.n_to, {
      nFrom: leg.n_from,
      nTo: leg.n_to,
      first: leg.first ?? null,
      last: leg.last ?? null,
      distanceM: leg.distance_m,
      durationS: leg.duration_s ?? null,
      source: leg.source,
      style: leg.style ?? null,
    });
  }
  const legs = stops.map((stop) => byArrival.get(stop.n) ?? null);

  // Projected once for the whole walk, so the monotonic search still holds for
  // the stops whose leg told us nothing.
  const guessed = anchorStops(line, stops.map((stop) => stop.at));
  const anchors = stops.map((_, i) => {
    const told = legs[i]?.last;
    return typeof told === 'number' ? clampVertex(told, line.length) : (guessed[i] ?? 0);
  });

  return { line, cum: cumulative(line), stops, anchors, legs };
}

function clampVertex(vertex: number, length: number): number {
  if (length === 0) return 0;
  return Math.min(Math.max(Math.trunc(vertex), 0), length - 1);
}

/**
 * Which stop the visitor is walking TOWARDS, or `null` when the walk is done.
 *
 * The first one not yet reached, and it is deliberately a function of
 * `arrived` ALONE — never of whether a position has arrived. `arrived` is
 * filled by the board's `current` as well as by the automaton, so this answers
 * correctly before the first GPS fix, which is the state the overlay opens in.
 * Reading `TrackState.nextStop` there instead said «all done» over a walk
 * nobody had started: its initial `null` means «not computed», and the two
 * nulls are not the same fact.
 */
export function nextStopIndex(state: TrackState, route: RouteModel): number | null {
  const index = route.stops.findIndex((stop) => !state.arrived.includes(stop.id));
  return index >= 0 ? index : null;
}

/**
 * How far the visitor still has to walk to a stop, and whether that number is
 * the DRAWN path or partly a straight line.
 *
 * **It is two segments, and leaving out the first is the bug this replaces.**
 * Live on dev: standing ~250 m before the line even begins, the hint read «До
 * «Большой каскад» 0 м» — the projection clamps to the first vertex (`t = 0`),
 * the stop is anchored at that same vertex, and the along-line part between
 * them is genuinely zero. What the visitor has to walk is the stretch to the
 * PATH plus the stretch along it, so both are counted.
 *
 * Off the path that first stretch is a straight line nobody routed, so the
 * number is marked approximate. The bar is `backOnRouteM` — the repository's
 * own «you are on the route» threshold, reused rather than restated, so the
 * hint and the off-route detector cannot start disagreeing about where the
 * path is.
 */
export function distanceToStop(
  route: RouteModel,
  snap: Snap | null,
  here: LngLat,
  index: number,
  cfg: TrackConfig = DEFAULT_TRACK_CONFIG,
): { metres: number; exact: boolean } | null {
  const stop = route.stops[index];
  if (!stop) return null;
  // No line to walk along: the honest number is the one straight line there is.
  if (!snap) return { metres: Math.round(distanceM(here, stop.at)), exact: false };

  const toPath = distanceM(here, snap.at);
  const along = distanceAlongTo(route.line, route.cum, snap, route.anchors[index] ?? 0);
  return {
    metres: Math.round(toPath + along),
    exact: toPath <= cfg.backOnRouteM,
  };
}

/**
 * One GPS fix against the route.
 *
 * Returns `prev` UNCHANGED (by identity) when the fix carries no information,
 * so a caller can skip a store write on it.
 */
export function step(
  prev: TrackState,
  route: RouteModel,
  fix: Fix,
  cfg: TrackConfig = DEFAULT_TRACK_CONFIG,
): TrackState {
  if (!Number.isFinite(fix.accuracyM) || fix.accuracyM > cfg.maxAccuracyM) return prev;

  const here: LngLat = [fix.lng, fix.lat];
  const snap = snapToPolyline(route.line, here);

  const next: TrackState = { ...prev, snap: snap ?? prev.snap, emit: null };

  // ── arrival ──────────────────────────────────────────────────────────────
  // By AIR, not along the line: somebody standing in front of Монплезир is at
  // Монплезир whether or not they walked the drawn path to get there.
  //
  // TWO nearest stops are wanted and they are different questions. Arrival asks
  // «which stop am I reaching», so it may only ever consider one NOT YET
  // reached: a park's alleys loop, and with the globally nearest stop being an
  // already-visited neighbour the stop the visitor is genuinely standing at was
  // never even tested — it could be walked past, inside the radius, for the
  // whole hold, and never marked. Leaving the route asks «what is near you»,
  // which is about the place and not about the walk, so that one is the nearest
  // stop of any kind.
  let nearestIndex = -1;
  let nearestM = Infinity;
  let targetIndex = -1;
  let targetM = Infinity;
  route.stops.forEach((stop, index) => {
    const d = distanceM(here, stop.at);
    if (d < nearestM) {
      nearestM = d;
      nearestIndex = index;
    }
    if (!prev.arrived.includes(stop.id) && d < targetM) {
      targetM = d;
      targetIndex = index;
    }
  });
  const nearest = nearestIndex >= 0 ? route.stops[nearestIndex]! : null;
  const target = targetIndex >= 0 ? route.stops[targetIndex]! : null;

  if (target && targetM <= cfg.arriveRadiusM) {
    if (prev.nearSince?.stopId !== target.id) {
      next.nearSince = { stopId: target.id, since: fix.at };
    } else if (fix.at - prev.nearSince.since >= cfg.arriveHoldMs) {
      next.arrived = [...prev.arrived, target.id];
      next.nearSince = null;
      next.emit = {
        kind: 'arrived',
        stopId: target.id,
        index: targetIndex,
        distanceM: Math.round(targetM),
      };
    }
  } else {
    next.nearSince = null;
  }

  // ── leaving the route ────────────────────────────────────────────────────
  if (snap) {
    if (prev.offRoute) {
      if (snap.offRouteM <= cfg.backOnRouteM) {
        next.offRoute = false;
        next.offSince = null;
        // Never overwrites an arrival: reaching a stop is the bigger news, and
        // `back_on_route` is local anyway.
        if (!next.emit) next.emit = { kind: 'back_on_route' };
      }
    } else if (snap.offRouteM > cfg.offRouteM) {
      if (prev.offSince === null) {
        next.offSince = fix.at;
      } else if (fix.at - prev.offSince >= cfg.offRouteHoldMs) {
        next.offRoute = true;
        next.offSince = null;
        if (!next.emit) {
          next.emit = {
            kind: 'off_route',
            distanceM: Math.round(snap.offRouteM),
            nearestStopId: nearest?.id ?? null,
          };
        }
      }
    } else {
      next.offSince = null;
    }
  }

  // ── where we are on the walk ─────────────────────────────────────────────
  const pending = nextStopIndex(next, route);
  next.nextStop = pending;
  next.currentLeg = pending ?? Math.max(0, route.stops.length - 1);
  const reach = pending !== null ? distanceToStop(route, snap, here, pending, cfg) : null;
  next.toNextM = reach?.metres ?? null;
  next.toNextExact = reach?.exact ?? false;

  return next;
}

/**
 * Folds the board's own `current` into the local set.
 *
 * `current` is the `n` of the pin the visitor is walking TOWARDS, so everything
 * numbered below it has been reached. **There is no second «visited» list**, and
 * that is the point of the shape: two lists are two answers to «где мы», and the
 * one the renderer trusted would be decided by arrival order.
 *
 * The frontend marks a stop reached the moment it detects the arrival, because
 * a card that waits for a round-trip feels broken; the authoritative answer
 * comes with the next board frame and confirms it. The merge is a UNION rather
 * than a replacement — a local arrival the guide has not acknowledged yet is a
 * fact about the visitor, not a guess — so the board can only ever add.
 */
export function syncCurrent(
  state: TrackState,
  route: RouteModel,
  current: number | null | undefined,
): TrackState {
  if (typeof current !== 'number') return state;
  const merged = [...state.arrived];
  for (const stop of route.stops) {
    if (stop.n < current && !merged.includes(stop.id)) merged.push(stop.id);
  }
  if (merged.length === state.arrived.length) return state;
  return { ...state, arrived: merged };
}
